import { useState, useRef, useCallback } from "react";

interface AnimationEvent {
  type: 'badge_earned' | 'quest_completed' | 'level_up';
  data: any;
  id: string;
}

export const useAnimationManager = () => {
  const [activeAnimation, setActiveAnimation] = useState<AnimationEvent | null>(null);
  const animationQueue = useRef<AnimationEvent[]>([]);
  const isProcessing = useRef(false);

  const queueAnimation = useCallback((type: AnimationEvent['type'], data: any) => {
    const id = `${type}_${Date.now()}_${Math.random()}`;
    const animation: AnimationEvent = { type, data, id };
    
    animationQueue.current.push(animation);
    processQueue();
  }, []);

  const processQueue = useCallback(() => {
    if (isProcessing.current || animationQueue.current.length === 0) {
      return;
    }

    isProcessing.current = true;
    const nextAnimation = animationQueue.current.shift()!;
    setActiveAnimation(nextAnimation);
  }, []);

  const completeAnimation = useCallback(() => {
    setActiveAnimation(null);
    isProcessing.current = false;
    
    // Process next animation in queue after a short delay
    setTimeout(() => {
      processQueue();
    }, 500);
  }, [processQueue]);

  const queueBadgeEarned = useCallback((badgeSlug: string) => {
    queueAnimation('badge_earned', { badgeSlug });
  }, [queueAnimation]);

  const queueQuestCompleted = useCallback((questTitle: string, xpAwarded: number) => {
    queueAnimation('quest_completed', { questTitle, xpAwarded });
  }, [queueAnimation]);

  const queueLevelUp = useCallback((newLevel: number) => {
    queueAnimation('level_up', { newLevel });
  }, [queueAnimation]);

  return {
    activeAnimation,
    completeAnimation,
    queueBadgeEarned,
    queueQuestCompleted,
    queueLevelUp
  };
};